import React from 'react';
import Reactor from 'data/reactor';
import { Immutable } from 'nuclear-js';
import moment from 'moment';
import PropTypes from 'prop-types';
import CreateReactClass from 'create-react-class';

import RuntimeActions from 'data/app-runtime/actions';
import StyleUtils from 'utils/styles';
import TermUtils from 'utils/terms';
import popover from 'utils/popover';
import CommonStrings from 'resources/common-strings';
import Analytics from 'utils/analytics';
import HelpBox from 'components/generic/help/index';
import HelpText from './help-text';
import CommonValues from 'resources/common-values';
import Getters from 'data/getters';

const reqContext = require.context('../../../../resources/images', false, /^\.\/.*\.svg$/);

export default CreateReactClass({
    displayName: 'subject-tab',

    propTypes: {
        clearUnsavedData: PropTypes.func.isRequired,
        isFinalTab: PropTypes.bool,
        highlights: PropTypes.instanceOf(Immutable.List),
        selectedSubject: PropTypes.instanceOf(Immutable.Map),
        showHighlights: PropTypes.bool,
        showSaveDataPrompt: PropTypes.func,
        studentBid: PropTypes.string.isRequired,
        subject: PropTypes.object.isRequired,
        subjectIndex: PropTypes.number.isRequired,
        termIsCurrent: PropTypes.bool.isRequired,
        termIsMostRecent: PropTypes.bool.isRequired,
        testEvent: PropTypes.instanceOf(Immutable.Map),
        testEventIsCurrent: PropTypes.bool.isRequired,
        toggleHighlightsFn: PropTypes.func.isRequired,
        unsavedData: PropTypes.bool.isRequired,
    },

    mixins: [Reactor.ReactMixin],

    getDataBindings() {
        return {
            animationIsRunning: Getters.runtime.animationIsRunning,
        };
    },

    selectSubject(event) {
        if (
            (event.currentTarget &&
                event.currentTarget.dataset &&
                event.currentTarget.dataset.bid &&
                event.currentTarget.dataset.bid == this.props.selectedSubject.get('bid')) ||
            this.state.animationIsRunning
        ) {
            return;
        }

        const selectActions = () => {
            Reactor.batch(() => {
                RuntimeActions.setUserAction('changing-subject');
                RuntimeActions.changeSelectedSubject(this.props.subject, true, true);
                RuntimeActions.setAnimationIsRunning(true);
                popover.closeChartPopoverBoxes();
            });
        };

        const tabName = this.props.subject.get('shortName')
            ? this.props.subject.get('shortName')
            : this.props.subject.get('description').toLowerCase();
        Analytics.recordButtonClickWithExpandedModuleAsLabel(
            CommonValues.SectionIDs.subjectTab,
            `select-subject-${tabName.toLowerCase()}`
        );

        if (this.props.unsavedData && this.props.showSaveDataPrompt) {
            this.props
                .showSaveDataPrompt()
                .then(() => {
                    selectActions();
                    this.props.clearUnsavedData();
                })
                .catch(() => {
                    /* do nothing */
                });
        } else {
            selectActions();
        }
    },

    toggleHighlightsVisibility(event) {
        Analytics.recordButtonClick(CommonValues.SectionIDs.highlights, this.props.showHighlights ? 'show' : 'hide');
        this.props.toggleHighlightsFn();
    },

    helpText() {
        return <HelpText />;
    },

    crossbarEventHandler(event) {
        event.stopPropagation();
    },

    render() {
        const tabName = this.props.subject.get('shortName')
            ? this.props.subject.get('shortName')
            : this.props.subject.get('description').toLowerCase();
        const testScore = this.props.testEvent
            ? this.props.testEvent.get('score', CommonStrings.blanks)
            : CommonStrings.blanks;
        const percentile = this.props.testEvent
            ? this.props.testEvent.getIn(['statusNorms', 'studentPercentile'], CommonStrings.blanks)
            : CommonStrings.blanks;
        const tabColor =
            percentile != CommonStrings.blanks ? StyleUtils.quintileClass(percentile) : StyleUtils.quintileClass(0);
        const tabClass = `subject-tab ${tabName} ${tabColor} ${this.props.testEvent ? '' : 'no-test-event'} ${
            this.props.testEvent && !this.props.testEventIsCurrent ? 'test-event-not-current' : ''
        }`;
        const tabStyle = { '--tab-index': this.props.subjectIndex };
        const termOfTest = this.props.testEvent
            ? TermUtils.fullTextFromTermNumber(this.props.testEvent.get('termNumber'))
            : '';
        const responseDisengagedPercentage =
            this.props.testEvent && this.props.testEvent.get('responseDisengagedPercentage')
                ? Math.round(this.props.testEvent.get('responseDisengagedPercentage')) + '%'
                : 'N/A';
        const impactOfDisengagement =
            this.props.testEvent && this.props.testEvent.get('impactOfDisengagement')
                ? Math.round(this.props.testEvent.get('impactOfDisengagement'))
                : 'N/A';
        const disengagedPercentageId = `percentage-disengaged-${tabName}`;
        const impactDisengagedId = `impact-of-disengagement-${tabName}`;
        let clickEvent = this.selectSubject;
        let details = [];
        const subjectBid = this.props.subject.get('bid', 0);
        if (this.props.testEvent) {
            const standardError = this.props.testEvent.get('standardError');
            const testRange = Math.round(testScore - standardError) + '-' + Math.round(testScore + standardError);
            const testDate = moment(new Date(this.props.testEvent.get('testDate'))).format('l');
            const testDuration = Math.round(this.props.testEvent.get('testDurationMs') / 60000);
            details = [
                <span key="error" data-testid={`${subjectBid}-standard-error`}>
                    Standard Error: +/- {this.props.testEvent.get('standardError')}
                </span>,
                <span key="range" data-testid={`${subjectBid}-range`}>
                    Possible range: {testRange}
                </span>,
                <span key="date-time" data-testid={`${subjectBid}-date`}>
                    {testDate} - {testDuration} minutes{' '}
                </span>,
                <span
                    id={disengagedPercentageId}
                    key="response-disengaged-percentage"
                    data-testid={`${subjectBid}-disengagement-percentage`}
                >
                    {CommonStrings.disengagement.disengagementPercentage}: {responseDisengagedPercentage}
                </span>,
                <span
                    id={impactDisengagedId}
                    key="impact-of-disengagement"
                    data-testid={`${subjectBid}-disengagement-impact`}
                >
                    {CommonStrings.disengagement.impactOfDisengagement}: {impactOfDisengagement}
                </span>,
                <span id={`test-name-${tabName}`} key="test-name" data-testid={`${subjectBid}-testname`}>
                    {this.props.testEvent.get('testName')}
                </span>,
                <span key="term" className="details-test-term" data-testid={`${subjectBid}-test-term`}>
                    {termOfTest}
                </span>,
            ];
        } else {
            details = [
                <span key="no-results">
                    <br />
                    No test results available
                </span>,
            ];
            if (!this.props.termIsMostRecent && !this.props.termIsCurrent) {
                clickEvent = null;
            }
        }
        const highlightsText = this.props.showHighlights
            ? CommonStrings.highlights.closeHighlights
            : CommonStrings.highlights.openHighlights;

        const highlightsBar =
            this.props.highlights && this.props.highlights.size > 0 ? (
                <div className="open-highlights-button" onClick={this.toggleHighlightsVisibility}>
                    <span className="triangle">&#9654;</span> {highlightsText}
                </div>
            ) : null;

        return (
            <div
                className={tabClass}
                style={tabStyle}
                data-testid={`${tabName}-tab`}
                data-name={tabName}
                data-bid={subjectBid}
                onClick={clickEvent}
            >
                <header>{this.props.subject.get('description')}</header>
                <div className="subject-data">
                    <HelpBox
                        id={`${tabName}-subject-tab-${this.props.studentBid}`}
                        appLevelCssClassName="show-subject-tab-help-box"
                        helpContentsRenderer={this.helpText}
                        moduleName={CommonValues.SectionIDs.subjectTab}
                        helpLabel={tabName.toLowerCase() + '-help'}
                        additionalOffset={this.props.isFinalTab === true}
                        adjustOverflow={this.props.isFinalTab === true}
                    />
                    <div className="score-container">
                        <img
                            className="subject-icon screen-only"
                            src={reqContext(CommonValues.scaleClass[this.props.subject.get('bid')])}
                        />
                        <img
                            className="subject-icon print-only"
                            src={reqContext(CommonValues.scalePrintClass[this.props.subject.get('bid')])}
                        />
                        <span className="test-score" data-testid={`${subjectBid}-test-score`}>
                            {testScore}
                        </span>
                    </div>
                    <div className="details">{details}</div>
                </div>
                <span className="test-term">{termOfTest}</span>
                <div className="tab-crossbar" onAnimationEnd={this.crossbarEventHandler}></div>
                {highlightsBar}
            </div>
        );
    },
});
